import Server from '../../api/server'
import {
  ALL_TRAVEL_POLICY_ROUTE,
  TRAVEL_POLICY_WITH_ID_ROUTE,
  TRAVEL_EVENTS_ROUTE,
  TRAVEL_LOCATIONS_ROUTE,
  TRAVEL_MEMBERS_ROUTE,
  TRAVEL_POLICY_ROUTE,
} from '../../strings/api-consts'
import { travelRiskOptions } from '../../strings/dropdown-options'
import { SelectOption } from '../../types/DropDownTypes'
import {
  DeleteTravelPolicyRequest,
  DeviceTravelEventHistory,
  DeviceTravelHistory as DeviceTravelHistory,
  DeviceTravelPolicy,
  TravelLocationHistory,
} from '../../types/TravelTypes'
import { CustomThunkDispatch } from '../../types/UserTypes'
import { reportErrorToDatadog } from '../../utils/error-logging'
import {
  setTravelMembersTotalPages,
  setTravelPolicy,
  setTravelMembers,
  setMapLocations,
  setTravelMembersTableCurrentPage,
  TravelMembersFilters,
  setTravelLocationOptions,
  setTravelPolicies,
  setTravelEvents,
  setTravelEventsTableCurrentPage,
  setTravelEventsTotalPages,
  TravelEventsFilters,
} from './travel.slice'

const server = new Server()

export const fetchTravelPolicy = () => async (dispatch: CustomThunkDispatch) => {
  return await server
    .get(TRAVEL_POLICY_ROUTE, dispatch)
    .then((res: DeviceTravelPolicy) => {
      dispatch(setTravelPolicy(res))
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      dispatch(setTravelPolicy(undefined))
    })
}

export const fetchAllTravelPolicies = () => async (dispatch: CustomThunkDispatch) => {
  return await server
    .get(ALL_TRAVEL_POLICY_ROUTE, dispatch)
    .then((res: DeviceTravelPolicy[]) => {
      dispatch(setTravelPolicies(res))
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      dispatch(setTravelPolicies(undefined))
    })
}

export const fetchTravelPolicyWithId = (devicePolicyId: number) => async (dispatch: CustomThunkDispatch) => {
  return await server
    .post(TRAVEL_POLICY_WITH_ID_ROUTE, { policyId: devicePolicyId }, dispatch)
    .then((res) => {
      if (!res) {
        throw new Error('Response is undefined')
      }
      return res.json()
    })
    .then((res: DeviceTravelPolicy) => {
      dispatch(setTravelPolicy(res))
      return res
    })
    .catch((err) => {
      reportErrorToDatadog(err.message)
      dispatch(setTravelPolicy(undefined))
      return undefined
    })
}

export const updateTravelPolicy = (payload: DeviceTravelPolicy) => async (dispatch: CustomThunkDispatch) => {
  return await server
    .post(TRAVEL_POLICY_ROUTE, payload, dispatch)
    .then((res) => {
      if (res?.status === 202) {
        return { error: undefined }
      }
      return res?.json().then((data) => {
        if (!res.ok) {
          return { error: data.error || 'Unknown error' }
        } else {
          return { error: undefined }
        }
      })
    })
    .catch((error) => {
      reportErrorToDatadog(error.message)
      return { error: error.message }
    })
}

export const deleteTravelPolicy =
  (deleteTravelPolicyRequest: DeleteTravelPolicyRequest) => async (dispatch: CustomThunkDispatch) => {
    return server
      .delete(TRAVEL_POLICY_ROUTE, dispatch, deleteTravelPolicyRequest)
      .then((response) => {
        return response?.ok ?? false
      })
      .catch(() => {
        return false
      })
  }

export const fetchTravelDevices =
  (filters: TravelMembersFilters, page: number = 0, sortColumn?: string, sortOrder?: string) =>
  async (dispatch: CustomThunkDispatch) => {
    let url = TRAVEL_MEMBERS_ROUTE + `?page=${page ? page + 1 : 1}&pageSize=${40}`
    if (filters.countries.length > 0) {
      const countryParam = filters.countries.map((country: SelectOption) => `&country=${country.value}`).join('')
      url += countryParam
    }

    if (filters.risk.length > 0) {
      const riskParam = filters.risk.map((risk: SelectOption) => `&risk=${risk.value}`).join('')
      url += riskParam
    }

    if (filters.travelGuide.length > 0) {
      const travelGuideParam = filters.travelGuide
        .map((travelGuide: SelectOption) => `&travelGuide=${travelGuide.value}`)
        .join('')
      url += travelGuideParam
    }
    if (sortColumn === 'memberName') sortColumn = 'travelMember'
    if (sortColumn) {
      url += `&sortColumn=${sortColumn}`
      if (sortOrder) {
        url += `&sortOrder=${sortOrder}`
      }
    }

    return server
      .get(url, dispatch)
      .then((travel: DeviceTravelHistory) => {
        dispatch(setTravelMembersTableCurrentPage(page))
        dispatch(setTravelMembersTotalPages(travel.travelMembers?.totalPages))
        dispatch(setTravelMembers(travel.travelMembers?.travelDevices ?? []))
        if (travel?.travelLocations && travel.travelLocations?.length > 0) {
          const options = travel?.travelLocations
            .map((location) => {
              return {
                label: location.country,
                value: location.country,
                color: travelRiskOptions.find((option) => option.value === location.riskLevel)?.color,
                bgColor: travelRiskOptions.find((option) => option.value === location.riskLevel)?.bgColor,
              } as SelectOption
            })
            .sort((a, b) => a.label.localeCompare(b.label))
          dispatch(setTravelLocationOptions(options))
        }
      })
      .catch((error) => {
        reportErrorToDatadog(error.message)
        return false
      })
  }

export const fetchTravelEvents =
  (filters: TravelEventsFilters, page: number = 0, sortColumn?: string, sortOrder?: string) =>
  async (dispatch: CustomThunkDispatch) => {
    let url = TRAVEL_EVENTS_ROUTE + `?page=${page ? page + 1 : 1}&pageSize=${40}`
    if (filters.countries.length > 0) {
      const countryParam = filters.countries.map((country: SelectOption) => `&country=${country.value}`).join('')
      url += countryParam
    }

    if (filters.risk.length > 0) {
      const riskParam = filters.risk.map((risk: SelectOption) => `&risk=${risk.value}`).join('')
      url += riskParam
    }

    if (filters.fromDateTime) {
      url += `&dateFrom=${filters.fromDateTime}`
    }

    if (filters.toDateTime) {
      url += `&dateTo=${filters.toDateTime}`
    }

    if (sortColumn) {
      url += `&sortColumn=${sortColumn}`
      if (sortOrder) {
        url += `&sortOrder=${sortOrder}`
      }
    }

    return server
      .get(url, dispatch)
      .then((travel: DeviceTravelEventHistory) => {
        dispatch(setTravelEventsTableCurrentPage(page))
        dispatch(setTravelEventsTotalPages(travel.totalPages))
        dispatch(setTravelEvents(travel.events ?? []))
        if (travel?.travelLocations && travel.travelLocations?.length > 0) {
          const options = travel?.travelLocations
            .map((location) => {
              return {
                label: location.country,
                value: location.countryCode,
                color: travelRiskOptions.find((option) => option.value === location.riskLevel)?.color,
                bgColor: travelRiskOptions.find((option) => option.value === location.riskLevel)?.bgColor,
              } as SelectOption
            })
            .sort((a, b) => a.label.localeCompare(b.label))
          dispatch(setTravelLocationOptions(options))
          dispatch(setMapLocations(travel?.travelLocations))
        }
      })
      .catch((error) => {
        reportErrorToDatadog(error.message)
        dispatch(setTravelEvents([]))
        return false
      })
  }

export const fetchMapLocations = () => async (dispatch: CustomThunkDispatch) => {
  return server
    .get(TRAVEL_LOCATIONS_ROUTE, dispatch)
    .then((location: TravelLocationHistory) => {
      dispatch(setMapLocations(location.travelLocations))
    })
    .catch((error) => {
      reportErrorToDatadog(error.message)
      return false
    })
}
